import React from "react"
import { Link } from "gatsby"
import { ThemeProvider } from "styled-components"
import componentThemeExtension from "utils/componentThemeExtension"
import { useIsHamburgerLargeOrHigher } from "utils/devices"
import { applyVideoCloudinaryTransforms } from "utils/applyCloudinaryTransforms"

import {
  SliderContainer,
  SliderMediaSection,
  SliderContentSection,
  SliderContent,
  SliderPreHeadingContainer,
  SliderPreHeadingText,
  SliderPreHeadingLogo,
  SliderHeading,
  SliderBody,
  SliderCardsRow,
  SliderCard,
  SliderCardTitle,
  SliderCardAuthor,
  SliderCollapsedOverlay,
  SliderCollapsedTitle,
  SliderTitleTop,
  SliderTitleBottom,
  SliderMobileStackedContainer,
  SliderMobileTopRow,
  SliderMobileImageSection,
  SliderMobileContentSection,
  SliderMobileCardsSection,
  SliderMobileCard,
  SliderButton,
  SliderVideo,
} from "./styles"

const SliderButtonComponent = ({ component, sliderIndex, isExpandedSlider }) => {
  const { text, targetUrl } = component

  const handleClick = () => {
    if (targetUrl) {
      window.open(targetUrl, "_blank")
    }
  }

  return (
    <SliderButton
      onClick={handleClick}
      sliderIndex={sliderIndex}
      tabIndex={isExpandedSlider ? 0 : -1}
    >
      {text}
    </SliderButton>
  )
}

// Helper function to get background image
const getBackgroundImage = (items) =>
  items[0]?.imageCrops?.crops?.[0]?.path ||
  items[0]?.image?.[0]?.secure_url ||
  (items[0]?.article?.previewImage &&
    (() => {
      const previewData = JSON.parse(items[0].article.previewImage)
      const imagePath = previewData?.crops?.[0]?.path || previewData?.path
      if (imagePath?.startsWith("https://")) {
        return imagePath
      }
      if (imagePath?.startsWith("img/")) {
        return `https://mindbodygreen-res.cloudinary.com/image/upload/${imagePath.replace(
          "img/",
          "",
        )}`
      }
      return imagePath
    })())

const Slider = ({
  isExpandedSlider,
  sliderIndex,
  items,
  preHeadingText,
  subheadingLogo,
  title,
  bodyText,
  subheading,
  button,
  backgroundColor,
  onTop,
  theme,
}) => {
  const isHamburgerLarge = !useIsHamburgerLargeOrHigher()

  const extractVideoUrl = (video) => {
    if (!video) return null

    let videoUrl = null

    if (video.videoObject && video.videoObject.length > 0) {
      videoUrl = video.videoObject[0].secure_url || video.videoObject[0].url || null
    } else {
      videoUrl = video.url || video.videoUrl || video.secure_url || null
    }

    videoUrl = applyVideoCloudinaryTransforms(videoUrl, `w_275,ac_none`)

    return videoUrl
  }

  const hasVideo = !!items[0]?.previewVideo

  if (isHamburgerLarge) {
    return (
      <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme || null)}>
        <SliderMobileStackedContainer sliderIndex={sliderIndex}>
          <SliderMobileTopRow>
            <SliderMobileImageSection
              backgroundImage={!hasVideo ? getBackgroundImage(items) : null}
            >
              {hasVideo && (
                <SliderVideo
                  src={extractVideoUrl(items[0].previewVideo)}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              )}
            </SliderMobileImageSection>
            <SliderMobileContentSection>
              <SliderPreHeadingContainer>
                <SliderPreHeadingText sliderIndex={sliderIndex}>
                  {preHeadingText &&
                    preHeadingText.childMarkdownRemark.html.replace(/(<([^>]+)>)/gi, "")}
                  {subheadingLogo?.length ? " | PRESENTED BY" : ""}
                </SliderPreHeadingText>
                {subheadingLogo?.length && (
                  <SliderPreHeadingLogo
                    src={subheadingLogo[0]?.secure_url || subheadingLogo[0]?.url}
                    alt="Presented by"
                  />
                )}
              </SliderPreHeadingContainer>
              <SliderHeading
                sliderIndex={sliderIndex}
                dangerouslySetInnerHTML={{ __html: title }}
              />
              {bodyText && (
                <SliderBody
                  sliderIndex={sliderIndex}
                  dangerouslySetInnerHTML={{ __html: bodyText.childMarkdownRemark.html }}
                />
              )}
              {subheading && (
                <SliderBody
                  sliderIndex={sliderIndex}
                  dangerouslySetInnerHTML={{ __html: subheading.childMarkdownRemark.html }}
                />
              )}
              {button && (
                <SliderButtonComponent
                  component={button}
                  sliderIndex={sliderIndex}
                  isExpandedSlider={isExpandedSlider}
                />
              )}
            </SliderMobileContentSection>
          </SliderMobileTopRow>

          <SliderMobileCardsSection>
            {items.slice(1, 3).map((item) => {
              const articleUrl = item.article?.url ? `/articles/${item.article.url}` : null
              const cardContent = (
                <SliderMobileCard key={item.id} sliderIndex={sliderIndex}>
                  <SliderCardTitle sliderIndex={sliderIndex}>
                    {item.headlineCard ||
                      item.article?.titles?.site ||
                      item.article?.titles?.body ||
                      "Article Title"}
                  </SliderCardTitle>
                  <SliderCardAuthor sliderIndex={sliderIndex}>
                    By {item.byline || item.article?.authors?.[0]?.name || "Author Name"}
                  </SliderCardAuthor>
                </SliderMobileCard>
              )

              return articleUrl ? (
                <Link key={item.id} to={articleUrl} style={{ textDecoration: "none" }}>
                  {cardContent}
                </Link>
              ) : (
                cardContent
              )
            })}
          </SliderMobileCardsSection>
        </SliderMobileStackedContainer>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme || null)}>
      <SliderContainer isExpanded={isExpandedSlider} sliderIndex={sliderIndex}>
        {/* Image Section */}
        <SliderMediaSection
          isExpanded={isExpandedSlider}
          sliderIndex={sliderIndex}
          backgroundImage={!hasVideo ? getBackgroundImage(items) : null}
        >
          {hasVideo && (
            <SliderVideo
              src={extractVideoUrl(items[0].previewVideo)}
              autoPlay
              muted
              loop
              playsInline
              width="100%"
              height="100%"
              poster={getBackgroundImage(items)}
            />
          )}
        </SliderMediaSection>
        <SliderContentSection isExpanded={isExpandedSlider} sliderIndex={sliderIndex}>
          <SliderContent isExpanded={isExpandedSlider}>
            {/* Pre-heading and heading always visible */}
            <SliderPreHeadingContainer>
              <SliderPreHeadingText sliderIndex={sliderIndex}>
                {preHeadingText &&
                  preHeadingText.childMarkdownRemark.html.replace(/(<([^>]+)>)/gi, "")}
                {subheadingLogo?.length ? " | PRESENTED BY" : ""}
              </SliderPreHeadingText>
              {subheadingLogo?.length && (
                <SliderPreHeadingLogo
                  src={subheadingLogo[0]?.secure_url || subheadingLogo[0]?.url}
                  alt="Presented by"
                />
              )}
            </SliderPreHeadingContainer>
            <SliderHeading
              isExpanded={isExpandedSlider}
              sliderIndex={sliderIndex}
              dangerouslySetInnerHTML={{ __html: title }}
            />

            {/* Body text appears after transition completes */}
            {bodyText && (
              <SliderBody
                isExpanded={isExpandedSlider}
                sliderIndex={sliderIndex}
                dangerouslySetInnerHTML={{ __html: bodyText.childMarkdownRemark.html }}
              />
            )}
            {subheading && (
              <SliderBody
                isExpanded={isExpandedSlider}
                sliderIndex={sliderIndex}
                dangerouslySetInnerHTML={{ __html: subheading.childMarkdownRemark.html }}
              />
            )}
            <SliderButtonComponent
              component={button}
              sliderIndex={sliderIndex}
              isExpandedSlider={isExpandedSlider}
            />
          </SliderContent>

          {/* Article Cards - appear after transition completes */}
          <SliderCardsRow isExpanded={isExpandedSlider}>
            {items.slice(1, 3).map((item) => {
              const articleUrl = item.article?.url ? `/articles/${item.article.url}` : null
              const cardContent = (
                <SliderCard key={item.id} sliderIndex={sliderIndex}>
                  <SliderCardTitle sliderIndex={sliderIndex}>
                    {item.headlineCard ||
                      item.article?.titles?.site ||
                      item.article?.titles?.body ||
                      "Article Title"}
                  </SliderCardTitle>
                  <SliderCardAuthor sliderIndex={sliderIndex}>
                    By {item.byline || item.article?.authors?.[0]?.name || "Author Name"}
                  </SliderCardAuthor>
                </SliderCard>
              )

              return articleUrl ? (
                <Link
                  key={item.id}
                  to={articleUrl}
                  style={{
                    textDecoration: "none",
                    flex: "1",
                  }}
                  tabIndex={isExpandedSlider ? 0 : -1}
                >
                  {cardContent}
                </Link>
              ) : (
                cardContent
              )
            })}
          </SliderCardsRow>
        </SliderContentSection>

        {/* Collapsed State Overlay - CSS-delay controlled */}
        <SliderCollapsedOverlay isExpanded={isExpandedSlider}>
          <SliderTitleTop sliderIndex={sliderIndex}>
            <div
              dangerouslySetInnerHTML={{
                __html: preHeadingText
                  ? preHeadingText.childMarkdownRemark.html
                      .split("<p>")[1]
                      .split("</p>")[0]
                      .split(" ")[0]
                  : "DIGITAL",
              }}
            />
          </SliderTitleTop>
          <SliderCollapsedTitle dangerouslySetInnerHTML={{ __html: title }} />
          <SliderTitleBottom sliderIndex={sliderIndex}>
            <div
              dangerouslySetInnerHTML={{
                __html: preHeadingText
                  ? preHeadingText.childMarkdownRemark.html
                      .split("<p>")[1]
                      .split("</p>")[0]
                      .split(" ")[1]
                  : "ISSUE",
              }}
            />
          </SliderTitleBottom>
        </SliderCollapsedOverlay>
      </SliderContainer>
    </ThemeProvider>
  )
}

export default Slider
